import { type FC, useState, memo } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { useServerVars } from '../shared/ServerVarsContext.tsx';
import { PhotoItem } from './PhotoItem/PhotoItem.tsx';
import { useGeneratePhotoGrid } from 'dibs-interiors-photos/exports/useGeneratePhotoGrid.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { usePhotosLikeDataRefetch } from 'dibs-interiors-photos/exports/usePhotosLikeDataRefetch.ts';

import styles from './PhotosSearchBrowseContent.scss';

const COLUMNS_PER_PAGE = {
    DESKTOP: 3,
    MOBILE: 2,
};

import { type PhotosSearchBrowseContent_photos$data as PhotosSearchBrowseContentPhotos } from './__generated__/PhotosSearchBrowseContent_photos.graphql.ts';

type PhotosSearchBrowseContentComponentProps = {
    originalUrl: string;
    userId: string;
    photos: PhotosSearchBrowseContentPhotos | null;
    onPhotoClick: (url: string) => void;
};

const PhotosSearchBrowseContentComponent: FC<PhotosSearchBrowseContentComponentProps> = props => {
    const { originalUrl, userId, onPhotoClick, photos } = props;
    const [toggledQuickView, setToggledQuickView] = useState<string | null>(null);
    const { isMobile } = useServerVars();
    const photosRef = (photos?.edges || []).map(photo => photo?.node || null)?.filter(filterNulls);
    const { photoLikeDataMap, isRefetching } = usePhotosLikeDataRefetch({ photosRef, userId });

    let columnsPerPage = COLUMNS_PER_PAGE.DESKTOP;
    if (isMobile) {
        columnsPerPage = COLUMNS_PER_PAGE.MOBILE;
    }

    const { photoGrid } = useGeneratePhotoGrid({ columnsPerPage, photoEdges: photos?.edges });

    return (
        <div className={styles.wrapper} data-tn="photos-content">
            {photoGrid.map((column, columnIndex) => (
                <div className={styles.column} key={`photo-column-${columnIndex}`}>
                    {column.map((photo, index) => {
                        const key = photo.serviceId || `${columnIndex}-${index}`;
                        const photoLikeData = photoLikeDataMap.get(photo?.serviceId || '');
                        return (
                            <PhotoItem
                                onQuickViewOpen={() => setToggledQuickView(key)}
                                onQuickViewClose={() => setToggledQuickView(null)}
                                isQuickViewOpen={toggledQuickView === key}
                                key={key}
                                originalUrl={originalUrl}
                                photo={photo}
                                onClick={onPhotoClick}
                                photoLikeData={photoLikeData}
                                isRefetchingPhotoLikes={isRefetching}
                            />
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export const PhotosSearchBrowseContent = memo(
    createFragmentContainer(PhotosSearchBrowseContentComponent, {
        photos: graphql`
            fragment PhotosSearchBrowseContent_photos on PhotosSearchBrowseQueryConnection {
                edges {
                    node {
                        ...PhotoItem_photo
                        ...usePhotosLikeDataRefetch_photos
                        serviceId
                    }
                }
            }
        `,
    })
);
